import { useRef, useState, useEffect, useCallback } from 'react';

type UseVoicePreviewResult = {
  playingVoiceKey: string;
  togglePreview: (voiceKey: string, previewAssetPath: string) => void;
  stopPreview: () => void;
};

/* @ngInject */
export default function useVoicePreview(): UseVoicePreviewResult {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadedVoiceKeyRef = useRef<string | null>(null);
  const playingVoiceKeyRef = useRef('');
  const [playingVoiceKey, setPlayingVoiceKey] = useState('');

  // Single setter that keeps the ref and the state in sync
  const setKey = useCallback((val: string) => {
    playingVoiceKeyRef.current = val;
    setPlayingVoiceKey(val);
  }, []);

  // Stable reference so the same function can be passed to removeEventListener
  const onEndedRef = useRef(() => setKey(''));

  const stopPreview = useCallback(() => {
    audioRef.current?.pause();
    setKey('');
  }, [setKey]);

  const togglePreview = useCallback((voiceKey: string, previewAssetPath: string) => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.addEventListener('ended', onEndedRef.current);
      audioRef.current = audio;
    }

    audioRef.current.pause();

    if (playingVoiceKeyRef.current === voiceKey) {
      setKey('');
      return;
    }

    // keep the same src when resuming so playback continues where it left off
    if (loadedVoiceKeyRef.current !== voiceKey) {
      audioRef.current.src = previewAssetPath;
      loadedVoiceKeyRef.current = voiceKey;
    }

    setKey(voiceKey);
    audioRef.current.play().catch(() => setKey(''));
  }, [setKey]);

  useEffect(() => () => {
    audioRef.current?.removeEventListener('ended', onEndedRef.current);
    stopPreview();
  }, [stopPreview]);

  return { playingVoiceKey, togglePreview, stopPreview };
}
